import {ChangeDetectionStrategy, Component, computed, inject, input, output} from '@angular/core';
import {DatePipe} from '@angular/common';
import {toSignal} from '@angular/core/rxjs-interop';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {
  CustomerProductHistoryEvent,
  CustomerProductHistorySummary,
  UserSelect,
} from '../../../../../../../../../api';
import {PartyAvatar} from '../../../../../../../../components/avatar/party-avatar.component';
import {
  buildHistoryChangeRows,
  EVENT_STYLES,
  EventStyle,
  HistoryChangeRow,
} from '../../../../customer-product-history.utils';

@Component({
  selector: 'elcia-customer-product-history-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, TranslatePipe, PartyAvatar],
  templateUrl: './customer-product-history-item.html',
  styleUrl: './customer-product-history-item.scss',
  host: {
    // Fully computed so a deleted product line carries no click affordance at all.
    '[class]': 'hostClasses()',
    '[attr.role]': 'isSelectable() ? "button" : null',
    '[attr.tabindex]': 'isSelectable() ? 0 : null',
    '(click)': 'select()',
    '(keydown.enter)': 'select()',
    '(keydown.space)': 'select(); $event.preventDefault()',
  },
})
export class CustomerProductHistoryItem {
  private readonly translate = inject(TranslateService);

  entry = input.required<CustomerProductHistorySummary>();
  isLast = input.required<boolean>();
  onSelect = output<number>();

  private readonly langChange = toSignal(this.translate.onLangChange, {initialValue: null});

  private readonly style = computed<EventStyle>(() => EVENT_STYLES[this.entry().event]);

  protected readonly eventLabelKey = computed(() => this.style().labelKey);
  protected readonly iconClass = computed(() => this.style().icon);
  protected readonly markerClasses = computed(() => this.style().marker);

  protected readonly parentProductName = computed(() => this.entry().parentProductName);

  // Read by the tooltip, where the truncated header cannot show the whole chain.
  protected readonly productLabel = computed(() => {
    const parentProductName = this.parentProductName();
    const productName = this.entry().productName;

    return parentProductName ? `${parentProductName} › ${productName}` : productName;
  });

  // An extra feature has no row of its own in the left column: the entry points at the licence carrying
  // it. Null once nothing is left to point at, the traced line and its licence both being gone.
  protected readonly targetCustomerProductId = computed(() =>
    this.entry().parentCustomerProductId ?? this.entry().customerProductId);

  protected readonly isSelectable = computed(() => this.targetCustomerProductId() != null);

  protected readonly hostClasses = computed(() => {
    // Same padding, radius and hover as the product rows in the left column, so both read as one screen.
    const base = 'flex gap-3 px-3 py-2.5 rounded transition-colors duration-200 motion-reduce:transition-none';
    if (!this.isSelectable()) return base;

    return `${base} cursor-pointer hover:bg-primary-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500`;
  });

  protected readonly authors = computed<UserSelect[]>(() => {
    const author = this.entry().createdBy;
    return author ? [author] : [];
  });

  protected readonly changes = computed<HistoryChangeRow[]>(() => {
    this.langChange();
    const entry = this.entry();
    if (entry.event !== CustomerProductHistoryEvent.UPDATED) return [];

    return buildHistoryChangeRows(entry.changes, this.translate);
  });

  protected select(): void {
    const customerProductId = this.targetCustomerProductId();
    if (customerProductId == null) return;

    this.onSelect.emit(customerProductId);
  }
}
